import { useQuery } from '@apollo/client';
import {
  Ability,
  AbilityTuple,
  ExtractSubjectType,
  MongoQuery,
  SubjectRawRule,
  SubjectType,
} from '@casl/ability';
import { createContextualCan } from '@casl/react';
import { isEmpty, isNil, omit } from 'lodash';
import { useContext, useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { PermissionsContext, PermissionsContextValue } from './PermissionsContext';

export type AbilityAction = 'create' | 'read' | 'update' | 'delete' | 'manage';
export type AbilitySubject =
  | 'Card'
  | 'Report'
  | 'ChannelMembership'
  | 'ThreadMessage'
  | 'SharedCardLink'
  | 'WorkflowTemplate'
  | 'ListWorkflowEntities'
  | 'CreateReportUrl'
  | 'ViewTemplate'
  | 'FileOperation';

export type AbilityType = AbilityTuple<AbilityAction, AbilitySubject>;

export enum PermissionReasonCode {
  STARTER_PLAN_REQUIRED = 'starter',
  PLUS_PLAN_REQUIRED = 'plus',
}

export const Can = createContextualCan(PermissionsContext.Consumer);

type Rule = SubjectRawRule<
  AbilityAction,
  SubjectType & ExtractSubjectType<AbilitySubject>,
  MongoQuery
>;

export interface UsePermissionResponse {
  enabled: boolean;
  reason?: PermissionReasonCode;
  loading?: boolean;
}

export function usePermissions(): any {
  const { data: globalPermissions } = useQuery<API.UserGlobalPermissionsQuery>(
    Queries.user.userGlobalPermissions,
  );
  return useMemo(() => globalPermissions?.caslAbility, [globalPermissions]);
}

export function usePermission(params: {
  action: AbilityAction;
  subject: AbilitySubject;
  channelId?: number;
  workflowTemplateId?: string;
  field?: string;
  requireChannelId?: boolean;
}): UsePermissionResponse {
  const { loading: permissionsLoading } = useQuery<API.UserGlobalPermissionsQuery>(
    Queries.user.userGlobalPermissions,
  );
  const globalAbility = useContext(PermissionsContext);
  return useMemo(() => {
    if (isNil(globalAbility)) {
      return { enabled: false, loading: true };
    }
    if (params.requireChannelId && isNil(params.channelId)) {
      return { enabled: false, loading: true };
    }
    if (permissionsLoading) {
      return { enabled: false, loading: true };
    }
    const ability = getAbility({
      globalAbility,
      channelId: params.channelId,
      workflowTemplateId: params.workflowTemplateId,
    });

    // Inspired by ability.can
    // https://github.com/stalniy/casl/blob/7980e5aa95b88f6903015c58424b37e516de248a/packages/casl-ability/src/PureAbility.ts#L18-L35
    const subjectType = ability.detectSubjectType(params.subject);
    const rules = ability.rulesFor(params.action, subjectType, params.field);
    const rule = rules.find(item => item.matchesConditions(params.subject));
    return { enabled: !!rule && !rule.inverted, reason: rule?.reason as PermissionReasonCode };
  }, [globalAbility, params, permissionsLoading]);
}

export const detectSubjectType = object => object.__typename;

function getAbility({
  globalAbility,
  channelId,
  workflowTemplateId,
}: {
  globalAbility: PermissionsContextValue;
  channelId?: number;
  workflowTemplateId?: string;
}): PermissionsContextValue {
  if (!isNil(channelId)) {
    return getChannelAbility(globalAbility, channelId);
  }
  if (!isNil(workflowTemplateId)) {
    return getWorkflowTemplateAbility(globalAbility, workflowTemplateId);
  }
  return globalAbility;
}

function getWorkflowTemplateAbility(
  globalAbility: PermissionsContextValue,
  workflowTemplateId: string,
): PermissionsContextValue {
  const rules = globalAbility.rules.filter(rule => {
    return (
      !isNil(rule.conditions?.workflowTemplateId) &&
      matchesWorkflowTemplate(rule.conditions.workflowTemplateId, workflowTemplateId)
    );
  });
  const transformedRules = isEmpty(rules)
    ? []
    : rules.map(rule => transformWorkflowTemplateRule(rule));
  return new Ability<AbilityType>(transformedRules, { detectSubjectType });
}

export function getChannelAbility(
  globalAbility: PermissionsContextValue,
  channelId: number,
): PermissionsContextValue {
  const rules = globalAbility.rules.filter(
    rule =>
      isNil(rule.conditions) ||
      matchesChannelAsNumber(rule.conditions.channelId, channelId) ||
      matchesChannelAsExpression(rule.conditions.channelId, channelId) ||
      matchesChannelAsNumber(rule.conditions['channel.id'], channelId),
  );
  const transformedRules = isEmpty(rules) ? [] : rules.map(rule => transformChannelRule(rule));
  return new Ability<AbilityType>(transformedRules, { detectSubjectType });
}

function transformChannelRule(rule: Rule): Rule {
  if (!isNil(rule.conditions)) {
    const transformedConditions = omit(rule.conditions, 'channelId', 'channel.id');
    return { ...rule, conditions: transformedConditions };
  }
  return rule;
}

function transformWorkflowTemplateRule(rule: Rule): Rule {
  if (!isNil(rule.conditions)) {
    const transformedConditions = omit(rule.conditions, 'workflowTemplateId');
    return { ...rule, conditions: transformedConditions };
  }
  return rule;
}

function matchesWorkflowTemplate(value: any, workflowTemplateId: string): boolean {
  return !isNil(value) && typeof value === 'object' && value['$in'].includes(workflowTemplateId);
}

function matchesChannelAsNumber(value: any, channelId: number): boolean {
  return !isNil(value) && typeof value === 'number' && value === channelId;
}

function matchesChannelAsExpression(value: any, channelId: number): boolean {
  return !isNil(value) && typeof value === 'object' && value['$eq'] === channelId;
}
